import type { UserType } from '@kanbu/schema';
import { Role } from '@kanbu/schema/enums';
import { Trans, useLingui } from '@lingui/react/macro';
import { createFileRoute } from '@tanstack/react-router';
import { getQueryKey } from '@trpc/react-query';

import { Alert } from '@/components/Alert';
import { TablePage } from '@/components/page/TablePage';
import { Protected } from '@/components/protected/Protected';
import { useUsersCols } from '@/features/users/useUsersCols';
import { useUser } from '@/hooks';
import { useCurrentOrganization } from '@/hooks/useCurrentOrganization';
import { useDetailRowHandler } from '@/hooks/useDetailRowHandler';
import { useSubscriptionUserLimit } from '@/hooks/useSubscriptionUserLimit';
import { useTableQuery } from '@/hooks/useTableQuery';
import { trpc, trpcClient } from '@/services/trpc';

export const Route = createFileRoute('/_baseLayout/users/')({
  component: UsersPage,
});

export function UsersPage() {
  const { t } = useLingui();

  const { user: currentUser } = useUser();
  const { organization } = useCurrentOrganization();
  const organizationId = organization?.id;

  // Badge the attributed partner rep (if any) in the user list.
  const { data: clientRep } = trpc.partnerLinks.getClientRep.useQuery(
    { organizationId: organizationId ?? '' },
    { enabled: Boolean(organizationId) },
  );

  const columns = useUsersCols(clientRep?.attributedRepUserId);

  const {
    canAddUser,
    maxUsers,
    isLoading: isLoadingLimits,
  } = useSubscriptionUserLimit(organizationId);

  const handleQuery = useTableQuery(trpcClient.users.findAll.query);

  const handleRow = useDetailRowHandler<UserType>(row => ({
    to: '/users/$userId/edit',
    params: {
      userId: row.original.id,
    },
  }));

  const showCreateButton = currentUser?.role === Role.SuperAdmin || canAddUser;

  return (
    <>
      {!isLoadingLimits && !canAddUser && (
        <div className='mb-4'>
          <Protected roles={[Role.SuperAdmin]}>
            <Alert variant='warning' title={t`User Limit Reached`}>
              <Trans>
                This organization has reached its user limit (
                {maxUsers ?? 'N/A'}). As a Super Admin, you can still add more
                users, bypassing this limit.
              </Trans>
            </Alert>
          </Protected>
          <Protected roles={[Role.Admin]}>
            <Alert variant='danger' title={t`User Limit Reached`}>
              <Trans>
                This organization has reached its maximum user limit (
                {maxUsers ?? 'N/A'}). Upgrade your plan to add more users.
              </Trans>
            </Alert>
          </Protected>
        </div>
      )}

      <TablePage
        title={t`Users`}
        breadcrumbs={[{ label: t`Users`, to: '/users' }]}
        createButton={
          showCreateButton
            ? {
                to: '/users/create',
                label: t`Create user`,
              }
            : undefined
        }
        columns={columns}
        queryKey={getQueryKey(trpc.users.findAll)}
        onQueryFn={handleQuery}
        onRow={handleRow}
      />
    </>
  );
}
